import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MessageCircleIcon, PlusIcon } from 'lucide-react';
import { useEnquiryModal } from '../../contexts/EnquiryModalContext';
import { contact } from '../../data/contact';
import type { Faq } from '../../types/content';
interface FaqGroup {
  id: string;
  label: string;
  items: Faq[];
}
interface CourseFaqProps {
  id?: string;
  groups: FaqGroup[];
  intro?: string;
}
const ease = [0.23, 1, 0.32, 1] as const;

// Course FAQ: sticky intro + topic filter on the left, card accordion on the right.
export function CourseFaq({
  id = 'faq',
  groups,
  intro = 'Everything students usually ask before joining.'
}: CourseFaqProps) {
  const {
    open: openEnquiry
  } = useEnquiryModal();
  const [topic, setTopic] = useState('all');
  const [openQ, setOpenQ] = useState<string | null>(groups[0]?.items[0]?.q ?? null);
  const tabs = [{
    id: 'all',
    label: 'All questions',
    count: groups.reduce((n, g) => n + g.items.length, 0)
  }, ...groups.map(g => ({
    id: g.id,
    label: g.label,
    count: g.items.length
  }))];
  const visible = topic === 'all' ? groups.flatMap(g => g.items) : groups.find(g => g.id === topic)?.items ?? [];
  return <section id={id} aria-labelledby={`${id}-title`} className="w-full bg-white px-2 py-2 md:px-4" data-id="element-240" data-mp="1hr2gx7tchhyg">
      <div className="rounded-[28px] bg-cream md:rounded-[40px]" data-id="element-241" data-mp="1drae7bqmu610">
        <div className="mx-auto grid max-w-content gap-10 px-5 py-16 md:px-8 md:py-24 lg:grid-cols-12 lg:gap-16" data-id="element-242" data-mp="2aghy6fbls72t">
          <div className="lg:col-span-4" data-id="element-243" data-mp="25gwr1hma4sf0">
            <div className="lg:sticky lg:top-[160px]" data-id="element-244" data-mp="25hyaoahd2n8c">
              <h2 id={`${id}-title`} data-id="element-245" data-mp="20q6ixwmgybgm">
                <span className="block type-h2-light text-ink" data-id="element-246" data-mp="20p5a7ysxe122">Questions,</span>
                <span className="block type-h2 text-brand" data-id="element-247" data-mp="1wo1vxgqj0d2y">answered</span>
              </h2>
              <p className="mt-5 type-body text-ink" data-id="element-248" data-mp="2t799vnj2c9kd">{intro}</p>

              <div role="tablist" aria-label="Filter questions by topic" className="mt-8 flex flex-wrap gap-2 lg:flex-col lg:items-start" data-id="element-249" data-mp="2ogaq2dyv8tcd">
                {tabs.map(tab => {
                const active = tab.id === topic;
                return <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setTopic(tab.id)} className={`inline-flex h-11 items-center gap-3 rounded-full px-5 type-button font-bold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand ${active ? 'bg-brand text-white' : 'bg-white text-ink hover:bg-mist'}`} data-id="element-250" data-mp="rhadscoczc9w">
                      {tab.label}
                      <span className={`type-small ${active ? 'text-white' : 'text-muted'}`} data-id="element-251" data-mp="3v0mwf5louv0o">{tab.count}</span>
                    </button>;
              })}
              </div>

              <div className="mt-10 hidden rounded-[22px] bg-white p-6 lg:block" data-id="element-252" data-mp="1t52yrhweo117">
                <p className="type-card-title text-ink" data-id="element-253" data-mp="3dphl1q7z6m24">Still have a question?</p>
                <p className="mt-2 type-card-desc text-body" data-id="element-254" data-mp="38rg1l46n0kq4">Our admissions team speaks 20+ languages and usually replies the same day.</p>
                <div className="mt-5 flex flex-wrap gap-3" data-id="element-255" data-mp="3nguegm9f9ka9">
                  <button type="button" onClick={() => openEnquiry()} className="inline-flex h-11 items-center rounded-[15px] bg-brand px-5 type-button font-bold text-white transition-colors duration-150 hover:bg-brand-hover" data-id="element-256" data-mp="3inj4ic9bcne4">
                    Ask our team
                  </button>
                  <a href={contact.whatsappLink} target="_blank" rel="noreferrer" className="inline-flex h-11 items-center gap-2 rounded-[15px] border-2 border-brand px-4 type-button font-bold text-ink transition-colors duration-150 hover:bg-cream" data-id="element-257" data-mp="58wkmxtet1am">
                    <MessageCircleIcon className="h-4 w-4 text-brand-press" aria-hidden="true" data-id="element-258" data-mp="28hosmjwm92se" />
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>

          <ul className="flex flex-col gap-3 lg:col-span-8" data-id="element-259" data-mp="2ie9zlas74nyv">
            {visible.map((item, index) => {
            const isOpen = openQ === item.q;
            const panelId = `${id}-panel-${index}`;
            return <li key={item.q} className={`rounded-[22px] transition-colors duration-200 ${isOpen ? 'bg-white shadow-card' : 'bg-white/60 hover:bg-white'}`} data-id="element-260" data-mp="1uq0ds8uzg07q" data-mpi={index}>
                  <h3 data-id="element-261" data-mp="1zns7n8f6twek">
                    <button type="button" aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpenQ(isOpen ? null : item.q)} className="flex w-full items-center gap-5 px-5 py-5 text-left md:px-7 md:py-6" data-id="element-262" data-mp="24g39whxehuaa">
                      <span className="flex-1 type-accordion text-ink" data-id="element-263" data-mp="3p9sbrxnuqpwy">{item.q}</span>
                      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors duration-200 ${isOpen ? 'bg-brand text-white' : 'bg-cream text-brand-press'}`} aria-hidden="true" data-id="element-264" data-mp="3la1mjydz4huj">
                        <PlusIcon className={`h-5 w-5 transition-transform duration-200 ${isOpen ? 'rotate-45' : ''}`} data-id="element-265" data-mp="36chkaz8gp0s2" />
                      </span>
                    </button>
                  </h3>
                  <AnimatePresence initial={false} data-id="element-266" data-mp="2xk78xeltyp98">
                    {isOpen ? <motion.div id={panelId} initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.22,
                  ease
                }} className="overflow-hidden" data-id="element-267" data-mp="2skug8d8a2nyq">
                        <p className="max-w-2xl px-5 pb-6 type-body text-body md:px-7 md:pb-7" data-id="element-268" data-mp="2dxma3qttt8tm">{item.a}</p>
                      </motion.div> : null}
                  </AnimatePresence>
                </li>;
          })}
          </ul>

          <div className="rounded-[22px] bg-white p-6 lg:hidden" data-id="element-269" data-mp="240znoxluq9ph">
            <p className="type-card-title text-ink" data-id="element-270" data-mp="2rpaknpoffp8h">Still have a question?</p>
            <p className="mt-2 type-card-desc text-body" data-id="element-271" data-mp="2wmsl0rodow4v">Our admissions team speaks 20+ languages and usually replies the same day.</p>
            <button type="button" onClick={() => openEnquiry()} className="mt-5 inline-flex h-11 items-center rounded-[15px] bg-brand px-5 type-button font-bold text-white transition-colors duration-150 hover:bg-brand-hover" data-id="element-272" data-mp="2iucpsd1py2ei">
              Ask our team
            </button>
          </div>
        </div>
      </div>
    </section>;
}